import {
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
  type CirclesChart,
} from '@shared/circlesChart';
import type {
  CirclesReplayInput,
  CirclesReplayInputs,
  CirclesSpinSample,
  ReplayOutcome,
} from '@shared/replay';
import {
  buildCirclesEngineConfig,
  findActiveSlider,
  findActiveSpinner,
  step,
  type CirclesCircleShakePayload,
  type CirclesEngineEvent,
  type CirclesEngineInput,
  type CirclesHpChangedPayload,
  type CirclesSliderTickPayload,
  type CirclesSpinTickPayload,
} from '@shared/engine/circles/core';
import {
  createCirclesInitialState,
  type CircleResolution,
  type CirclesEngineState,
} from '@shared/engine/circles/state';
import { comboMultiplier, computeAccuracy } from '@shared/scoring';
import { RunTimeline } from '../input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import {
  GOOD_SCORE,
  OK_SCORE,
  PERFECT_SCORE,
  SPINNER_INTENSITY_SATURATION_RPS,
} from '@shared/gameTypes';
import { spinIntensityColour } from '@shared/spinIntensity';
import {
  milestoneIsStar,
  milestoneTier,
  type MilestoneKind,
} from '@shared/progression';
import { AudioEngine, clampPlaybackRate } from '../audio/scheduler';
import { loadVolumePrefs } from '../audio/volumePrefs';
import { CirclesRenderer } from './circlesRenderer';
import type {
  CirclesFlashSpec,
  CirclesFloatingText,
  CirclesParticleEmission,
  CirclesScorePop,
  CirclesShakeSpec,
  CirclesShockwave,
} from './render/circlesRenderTypes';
import {
  CirclesReplayInputSource,
  PointerInputSource,
  type CirclesInputSource,
} from './circlesInputSource';
import { getStackOffsets } from './circlesStacking';
import { resetCirclesEngineStateForNextLoop } from '@shared/engine/circles/state';
import { LoopRunState } from '../loopManager';
import { tutorialHintBudget } from '../songProgress';

export type CirclesSessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

export interface CirclesMilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface CirclesGameSessionOptions {
  chart: CirclesChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  /** Pre-recorded inputs (for replay verifiers / debug). */
  replayInputs?: CirclesEngineInput[];
  onStatusChange?: (status: CirclesSessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: CirclesEngineEvent) => void;
  onMilestone?: (info: CirclesMilestoneInfo) => void;
  testHook?: boolean;
  enableLoops?: boolean;
}

const COMBO_MILESTONES = [10, 25, 50, 100];
const LEAD_IN_BEATS = 4;
/**
 * Wall-clock floor for the lead-in countdown. At fast BPMs, 4 beats
 * compresses below ~1s and the "ready / set / go" cue feels rushed;
 * we extend it so the player always gets ~2 seconds of pre-roll
 * before the first circle. osu! stable enforces a similar 2s
 * lead-in floor — that's the muscle-memory window players expect.
 */
const MIN_LEAD_IN_WALL_SECONDS = 2.0;

/**
 * Tuning constants for the expanding-ring shockwaves. Hit-radii are
 * expressed as multipliers of the circle's CSS-px radius so the wave
 * scales with the canvas / playfield, not a fixed pixel size. The
 * cursor shockwave uses absolute px because it isn't tied to any
 * playfield element.
 */
const SHOCKWAVE = {
  perfect: { startMul: 1.0, endMul: 4.5, life: 0.32, thicknessMul: 0.18, alpha: 0.85 },
  perfectSecondary: {
    startMul: 0.4,
    endMul: 6.0,
    life: 0.42,
    thicknessMul: 0.10,
    alpha: 0.55,
  },
  good: { startMul: 1.0, endMul: 3.0, life: 0.26, thicknessMul: 0.12, alpha: 0.6 },
  // OK ring is intentionally smaller / shorter than `good` — the
  // mercy-band 50 should land as "you caught it" rather than "great
  // hit." Visual weight needs to sit clearly below `good` so players
  // self-correct toward the tighter windows.
  ok: { startMul: 0.9, endMul: 2.2, life: 0.22, thicknessMul: 0.10, alpha: 0.5 },
  // Miss ring is bigger + slower than hits — the wave should feel
  // like a "thud" hitting the playfield, not a "ping". Red colour is
  // applied at spawn time (the verdict colour cycles for hits but
  // misses are always angry-red).
  miss: { startMul: 1.2, endMul: 3.8, life: 0.36, thicknessMul: 0.14, alpha: 0.7 },
  // Combo-break ring stacks on top of the regular miss wave at higher
  // combos. Wider radius + longer life so a 50-combo break visibly
  // RIPS through the playfield rather than ticking quietly.
  comboBreak: { startMul: 1.6, endMul: 7.5, life: 0.55, thicknessMul: 0.16, alpha: 0.6 },
  cursor: { startR: 4, endR: 32, life: 0.18, thickness: 1.8, alpha: 0.55 },
};

/**
 * Threshold above which a perfect hit spawns a second, layered ring
 * for extra punch. Sized to match
 * the existing big-combo break threshold so visual rewards and
 * penalties trigger at the same combo height.
 */
const SHOCKWAVE_DOUBLE_RING_COMBO = 25;

/**
 * Score-pop animation life (seconds). The target HUD location for
 * each pop is sourced from `renderer.getHudScoreAnchor()` so the arc
 * stays aligned with whatever the renderer currently calls "the
 * score readout" without this file needing to track HUD layout.
 */
const SCORE_POP_LIFE = 0.7;

/**
 * Score-roll-up easing rate. Closed-form `1 - exp(-rate * dt)` per
 * frame; `15` lands ~95% of an increment in ~200 ms — feels like
 * the brief's quintic-ease-out without a per-increment timeline.
 */
const SCORE_DISPLAY_LERP_RATE = 15;

/**
 * How many score units cross between consecutive score-roll-up ticks.
 * Picked so a single perfect (300 base) fires three ticks as it
 * counts up; a high-combo perfect fires more — audible cadence
 * scales with how impressive the hit was.
 */
const SCORE_TICK_INTERVAL = 100;

/**
 * Music-bus duck-pulse tunings. Soft + fast on a regular miss so the
 * SFX punch reads through; bigger + longer on a sizeable combo break
 * so the player feels the lost streak in the mix. Both auto-restore
 * to unity gain via {@link AudioEngine.duckMusicPulse}.
 */
const MUSIC_DUCK_MISS = { dip: 0.72, attackMs: 25, releaseMs: 90 };
const MUSIC_DUCK_COMBO_BREAK = { dip: 0.5, attackMs: 40, releaseMs: 280 };

/**
 * Coalesce per-tick "+N" floating-text pops on a spinner. We spawn a
 * combined pop once the accumulated `scoreDelta` exceeds this many
 * points OR {@link SPINNER_TICK_POP_INTERVAL_MS} has passed since the
 * last pop, whichever comes first. Without this guard a 60 Hz capture
 * stacks ~42 floats on-screen at once during a sustained spinner.
 */
const SPINNER_TICK_POP_THRESHOLD = 30;
const SPINNER_TICK_POP_INTERVAL_MS = 140;

/**
 * Map a circle's playfield X (0..512) to a stereo pan position
 * clamped to ±0.85. The headroom keeps far-edge circles from
 * pinning audio entirely to one ear, which becomes uncomfortable
 * on headphones during dense edge-of-playfield streams.
 */
function panForCirclesX(osuX: number): number {
  const normalised = osuX / OSU_PLAYFIELD_WIDTH;
  const centred = normalised * 2 - 1;
  return Math.max(-0.85, Math.min(0.85, centred * 0.85));
}

function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) {
    return baseBeats;
  }
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

/**
 * Circles-mode game session. Mirrors {@link
 * import('../session.js').GameSession}'s lifecycle so PlayPage code can
 * be paralleled cleanly between modes:
 *
 *   constructor → start() → tick loop → pause/resume → destroy
 *
 * Reuses the same {@link AudioEngine} (mode-agnostic), {@link
 * LoopRunState} (chart-agnostic — only needs `totalBeats`), and the
 * universal star/cup milestone vocabulary. The Circles-specific bits
 * are the engine, renderer, and pointer input source.
 */
export class CirclesGameSession {
  private chart: CirclesChart;
  private engineConfig: ReturnType<typeof buildCirclesEngineConfig>;
  private state: CirclesEngineState;
  private audio: AudioEngine;
  private renderer: CirclesRenderer;
  private inputSource: CirclesInputSource;
  private particleEmissions: CirclesParticleEmission[] = [];
  private flashes: CirclesFlashSpec[] = [];
  private floatingTexts: CirclesFloatingText[] = [];
  private shockwaves: CirclesShockwave[] = [];
  private scorePops: CirclesScorePop[] = [];
  private shake: CirclesShakeSpec = { amount: 0, decay: 0 };
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: CirclesSessionStatus = 'idle';
  private opts: CirclesGameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: CirclesEngineEvent[] = [];
  /**
   * Per-loop captured taps — one entry per loop attempted, with each
   * loop's beats relative to that loop's start. See the matching
   * `loopCapturedInputs` doc-comment in {@link import('../session.js').GameSession}.
   */
  private loopCapturedInputs: CirclesReplayInput[][] = [[]];
  /**
   * Per-loop captured spin samples (cursor positions during active
   * spinner windows). Parallel structure to {@link loopCapturedInputs}
   * so the replay submission can pair the two streams loop-by-loop.
   * Frames are only pushed while a spinner is active (gated by the
   * same {@link findActiveSpinner} predicate the engine uses) so
   * non-spinner sections don't bloat the replay.
   */
  private loopCapturedSpinSamples: CirclesSpinSample[][] = [[]];
  /** Anti-cheat timing recorder (real-time progression + isTrusted). */
  private timeline: RunTimeline;
  /**
   * Last whole-rotation count we've already played feedback (whoosh
   * SFX + floating "xN" text + shake) for, keyed by spinner id.
   * Lives on the session, not the engine, because it's purely a
   * presentation concern — the engine's
   * {@link CirclesEngineState.spinnerProgress} keeps the authoritative
   * fractional rotations regardless of what the client has already
   * "celebrated".
   */
  private spinnerLastNotifiedRotations: Map<string, number> = new Map();
  /**
   * Per-spinner accumulator for the coalesced "+N" floating-text pop.
   * The engine emits one tick per captured sample (~60 Hz), but
   * spawning a fresh floating text on every tick stacks ~42 floats on
   * screen at any moment near playfield centre. We accumulate the
   * scoreDelta until either the bucket clears
   * {@link SPINNER_TICK_POP_THRESHOLD} or
   * {@link SPINNER_TICK_POP_INTERVAL_MS} has passed since the last
   * pop, then emit a single combined "+N" floating text.
   */
  private spinnerScorePopBuckets: Map<string, { accumulated: number; lastPopAt: number }> =
    new Map();
  private lastBeatPulseBeat = -1;
  private currentBeatPulse = 0;
  private comboMilestonesHit = new Set<number>();
  private lastTickAt = performance.now();
  private loopState: LoopRunState;
  private enableLoops: boolean;
  private destroyed = false;
  private autoPaused = false;
  /**
   * Per-run budget for in-game teaching hints ("CLICK ON THE CIRCLE!",
   * "HOLD TO THE END!"). Each hint type is capped independently so a
   * struggling beginner still gets slider help even after they've used
   * up circle help.
   */
  private readonly maxTutorialHints = tutorialHintBudget();
  private clickCircleHintsShown = 0;
  private holdSliderHintsShown = 0;
  /**
   * Cached `prefers-reduced-motion` query result. Drives session-side
   * decisions about which decorative effects to push to the renderer
   * (e.g. cursor shockwaves are skipped entirely; hit shockwaves get
   * smaller radii). The renderer also reads the same media query on
   * its own end for effects it owns directly (cursor trail, particle
   * counts and shake suppression).
   */
  private reducedMotion = false;
  /**
   * Pentatonic step counter for the layered hit SFX's melodic top
   * note. Advances on every successful (non-miss) resolution; the
   * sparkle layer wraps modulo `COMBO_PENTATONIC_LENGTH` so a long
   * stream loops the scale, and resets to 0 on miss so the next
   * chain starts back at the root note.
   */
  private comboNoteCounter = 0;
  /**
   * Smoothly-eased score readout shown by the renderer. Lerps toward
   * {@link CirclesEngineState.score} via an exponential approach so
   * the HUD counts up satisfyingly after a hit instead of snap-changing.
   */
  private displayedScore = 0;
  /**
   * Last `floor(displayedScore / SCORE_TICK_INTERVAL)` we played a
   * tick SFX for. Only one tick fires per frame regardless of how
   * many boundaries the eased value crossed in this frame — a
   * milestone burst would otherwise stack hundreds of ticks into
   * one frame and turn into a buzz.
   */
  private lastScoreTickHundred = 0;
  /**
   * Canvas size in CSS px, kept in sync via a `ResizeObserver`. The
   * draw loop reads from this cache instead of calling
   * `getBoundingClientRect()` per frame — that call can synchronously
   * force a layout pass on mobile browsers, which shows up as
   * dropped frames on dense charts.
   */
  private cachedRect = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;

  constructor(opts: CirclesGameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildCirclesEngineConfig(this.chart);
    this.state = createCirclesInitialState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new CirclesRenderer(opts.canvas);
    this.attachResizeObserver();

    if (opts.replayInputs) {
      this.inputSource = new CirclesReplayInputSource(opts.replayInputs);
    } else {
      this.inputSource = new PointerInputSource(
        opts.canvas,
        () => this.audio.getCurrentBeat(),
        () => this.status === 'lead_in' || this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as {
        __beatHeavenCirclesTestHook?: CirclesGameSessionTestHook;
      }).__beatHeavenCirclesTestHook = this.buildTestHook();
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, { speedRamp: ramp });
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  async startNextLoop(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    if (this.status !== 'awaiting_next_loop') {
      return;
    }
    this.loopState.advanceLoop();
    resetCirclesEngineStateForNextLoop(this.state);
    // `engineConfig` is keyed off the chart, which doesn't change
    // between loops — reuse the existing config rather than
    // re-tessellating every slider's path on each loop wrap.
    this.lastBeat = 0;
    this.lastBeatPulseBeat = -1;
    this.comboMilestonesHit.clear();
    // Open a fresh capture bucket for the new loop. See the matching
    // comment in {@link import('../session.js').GameSession.startNextLoop}.
    this.loopCapturedInputs.push([]);
    this.loopCapturedSpinSamples.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);
    this.spinnerLastNotifiedRotations.clear();
    this.spinnerScorePopBuckets.clear();
    this.renderer.clearSpinDrawCache();
    this.renderer.clearLoopBoundFx();

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    await this.audio.stop();
    delete (window as unknown as {
      __beatHeavenCirclesTestHook?: CirclesGameSessionTestHook;
    }).__beatHeavenCirclesTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): CirclesSessionStatus {
    return this.status;
  }

  getCapturedReplay(): CirclesReplayInputs {
    return {
      loops: this.loopCapturedInputs.map((inputs, loopIdx) => {
        const spinSamples = this.loopCapturedSpinSamples[loopIdx] ?? [];
        // Omit `spinSamples` entirely on loops with no capture so the
        // replay JSON stays byte-identical to legacy pre-spin-scoring
        // submissions for non-spinner charts (no needless `[]` field
        // for old runs the server has been validating fine for months).
        return spinSamples.length > 0
          ? { inputs: inputs.slice(), spinSamples: spinSamples.slice() }
          : { inputs: inputs.slice() };
      }),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /** Anti-cheat timing stream for this run (null if nothing captured). */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }

  private setStatus(s: CirclesSessionStatus): void {
    if (this.status === s) {
      return;
    }
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') {
      return;
    }

    const currentBeat = this.audio.getCurrentBeat();
    // Web Audio glitches under heavy CPU (and Safari's tab-resume
    // races) can return NaN / ±Infinity for one frame. Drop the
    // whole tick before NaN gets a chance to leak into:
    //   - `cappedTarget` (Math.min(NaN, ...) = NaN),
    //   - `this.lastBeat` (Math.max(finite, NaN) = NaN, which would
    //     brick every subsequent frame even after the clock recovers),
    //   - the captured-input / spin-sample arrays (NaN beats break
    //     the server's monotonicity check on replay submit).
    // The engine's `step()` and `advanceTo()` have their own NaN
    // guards as belt-and-braces, but bailing here is the only way to
    // keep `lastBeat` clean.
    if (!Number.isFinite(currentBeat)) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }
    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      this.drawFrame(currentBeat, leadInBeat);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
    }

    this.tickBeatPulse(currentBeat, dt);

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    const rawInputs = this.inputSource.poll(cappedTarget);
    // Drop inputs queued during lead-in (negative-beat). Same reason as Pulse:
    // the input source can't see the lead-in state and we don't want
    // pre-song clicks to chunk HP via the empty-tap path the moment beat 0
    // ticks over.
    const playableInputs = rawInputs.filter((inp) => inp.beat >= 0);
    const clampedInputs: CirclesEngineInput[] = playableInputs.map((inp) => ({
      beat: Math.max(this.lastBeat, Math.min(inp.beat, cappedTarget)),
      x: inp.x,
      y: inp.y,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({ beat: inp.beat, x: inp.x, y: inp.y });
    }

    // Capture one cursor sample per frame at the live audio beat,
    // gated by either an active spinner or an active slider (both
    // consume {@link CirclesSpinSample} entries — the engine's
    // `processSpinSample` and `processSliderSample` look at different
    // hit-object kinds, but the sample shape is shared). Skipping the
    // capture during sections where neither is active keeps replays
    // small and side-steps the per-step sort cost on charts with no
    // spin/slide bodies. The pointer source is the only input variant
    // that exposes a live cursor — replay playback consumes recorded
    // samples, not live capture.
    const spinSamples: CirclesSpinSample[] = [];
    const pointerSourceForSpin =
      this.inputSource instanceof PointerInputSource ? this.inputSource : null;
    if (pointerSourceForSpin) {
      const cursorPlayfield = pointerSourceForSpin.getCursorPosition();
      const sampleBeat = Math.max(this.lastBeat, cappedTarget);
      const activeSpinner = findActiveSpinner(
        this.engineConfig.sortedHitObjects,
        this.state.resolvedIds,
        sampleBeat,
      );
      const activeSlider = findActiveSlider(
        this.engineConfig.sortedHitObjects,
        this.state.sliderProgress,
        sampleBeat,
      );
      if (cursorPlayfield && (activeSpinner || activeSlider)) {
        // `getCursorPosition()` already returns playfield (osu) coords
        // — the pointer source projects every pointermove through
        // `clientToPlayfield` before storing it, so no extra mapping
        // is needed here. `pressing` is the slider tracking gate; the
        // spinner path ignores it (rotations are angular, not held).
        const sample: CirclesSpinSample = {
          beat: sampleBeat,
          x: cursorPlayfield.x,
          y: cursorPlayfield.y,
          pressing: pointerSourceForSpin.isCursorPressed(),
        };
        spinSamples.push(sample);
        const activeLoopSpins =
          this.loopCapturedSpinSamples[this.loopCapturedSpinSamples.length - 1];
        activeLoopSpins.push(sample);
      }
    }

    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
      spinSamples,
    });
    // `Math.max` rather than blind assignment so a regressing audio
    // clock (Web Audio glitches under heavy CPU, Safari resume bugs)
    // can't push `this.lastBeat` backward. If it did, subsequent
    // input clamps `[lastBeat, cappedTarget]` would let new taps land
    // at beats earlier than already-captured taps, breaking the
    // monotonicity the server's replay validator now enforces.
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
    }

    this.detectAndFireMidRunMilestones();
    this.tickDisplayedScore(dt);

    this.drawFrame(this.state.beat, null);

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) {
      return;
    }
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  /**
   * Ease the displayed score toward the engine's authoritative
   * {@link CirclesEngineState.score} and fire one
   * {@link AudioEngine.scheduleScoreTick} per
   * {@link SCORE_TICK_INTERVAL}-unit boundary the eased value crossed
   * (max one tick per frame so a milestone burst doesn't smear into
   * a buzzy stream of stacked ticks).
   */
  private tickDisplayedScore(dt: number): void {
    const target = this.state.score;
    if (this.displayedScore < target) {
      const k = 1 - Math.exp(-SCORE_DISPLAY_LERP_RATE * dt);
      this.displayedScore += (target - this.displayedScore) * k;
      // Snap to target once we're within a half-unit so the readout
      // never settles on a fractional value the HUD rounds away.
      if (target - this.displayedScore < 0.5) {
        this.displayedScore = target;
      }
    } else if (this.displayedScore > target) {
      // Engine score never decreases mid-run, but loop reset paths
      // zero it; jump down rather than animating in reverse.
      this.displayedScore = target;
      this.lastScoreTickHundred = Math.floor(target / SCORE_TICK_INTERVAL);
      return;
    }
    const currentTickHundred = Math.floor(this.displayedScore / SCORE_TICK_INTERVAL);
    if (currentTickHundred > this.lastScoreTickHundred) {
      this.lastScoreTickHundred = currentTickHundred;
      this.audio.scheduleScoreTick();
    }
  }

  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.spawnMilestoneFx(completionMilestone);
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        return;
      }
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!isFinite(songTime)) {
      return null;
    }
    if (songTime >= 0) {
      return null;
    }
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }

  private tickBeatPulse(currentBeat: number, dt: number): void {
    const beatNumber = Math.floor(currentBeat);
    if (beatNumber !== this.lastBeatPulseBeat && currentBeat >= 0) {
      this.lastBeatPulseBeat = beatNumber;
      this.currentBeatPulse = 1;
    } else {
      const beatsPerSec = this.chart.bpm / 60;
      const decayPerSec = beatsPerSec * 1.6;
      this.currentBeatPulse = Math.max(0, this.currentBeatPulse - decayPerSec * dt);
    }
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
    }
  }

  /**
   * Seed `cachedRect` from the canvas's current bounds and attach a
   * `ResizeObserver` so subsequent updates only fire on actual
   * viewport changes. Replaces the per-frame `getBoundingClientRect`
   * call.
   */
  private attachResizeObserver(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(this.opts.canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null): void {
    // Pointer source exposes both cursor position and held-tap state;
    // non-pointer sources (test harness, replay) just get a static
    // cursor with no press cue. Narrow once via `instanceof` so the
    // typed handle propagates to both reads — assigning the boolean
    // to a separate variable would lose the narrowing.
    const pointerSource =
      this.inputSource instanceof PointerInputSource ? this.inputSource : null;
    const cursor = pointerSource ? pointerSource.getCursorPosition() : null;
    const cursorPressed = pointerSource ? pointerSource.isCursorPressed() : false;
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      beat,
      width: this.cachedRect.width,
      height: this.cachedRect.height,
      cursor,
      cursorPressed,
      particleEmissions: this.particleEmissions,
      flashes: this.flashes,
      floatingTexts: this.floatingTexts,
      shockwaves: this.shockwaves,
      scorePops: this.scorePops,
      shake: this.shake,
      beatPulse: this.currentBeatPulse,
      countdown: leadInBeat,
      paused: this.status === 'paused',
      displayedScore: this.displayedScore,
    });
  }

  /**
   * Push a floating "judgement / damage / milestone" text into the
   * renderer's pool. Spawned at `(x, y)` (typically the centre of the
   * hit object the text describes), then offset upward so the label
   * sits ABOVE the hit object's outline rather than inside it. The
   * default offset is sized to the chart's circle radius plus half
   * the rendered font height plus a small gap, so the label's bottom
   * sits clear of the ring regardless of font size.
   *
   * `vy` is the initial upward velocity (px/s); the renderer also
   * damps it by ×0.97 per frame so floaters decelerate to a hover
   * before fading. `offsetY` overrides the default radius-based
   * offset for callers that want pixel-precise placement (e.g.
   * `LIFELINE!` which spawns at playfield centre with no associated
   * circle).
   *
   * `jitterX` is suppressed for big floaters so the milestone label
   * lands exactly where the player's eye is anticipating.
   */
  private spawnFloatingText(
    text: string,
    x: number,
    y: number,
    colour: string,
    opts: { big?: boolean; vy?: number; scale?: number; offsetY?: number } = {},
  ): void {
    const big = opts.big ?? false;
    const life = big ? 1.0 : 0.75;
    const jitterX = big ? 0 : (Math.random() - 0.5) * 22;
    // Default offset clears the chart's circle radius (in CSS px),
    // plus half the rendered font height (so the text *bottom* sits
    // above the ring with middle baseline), plus a 12-18px gap. The
    // base font is 28/44px so half-height is 14/22px. Caller-specified
    // offsetY wins (e.g. centre-of-playfield milestone labels).
    const radiusCss = this.renderer.osuLengthToCss(this.chart.circleRadius);
    const fontHalfHeight = big ? 22 : 14;
    const gap = big ? 18 : 12;
    const defaultOffsetY = -(radiusCss + fontHalfHeight + gap);
    const offsetY = opts.offsetY ?? defaultOffsetY;
    this.floatingTexts.push({
      text,
      x: x + jitterX,
      y: y + offsetY,
      colour,
      vy: opts.vy ?? -50,
      scale: opts.scale ?? 1,
      life,
      maxLife: life,
      big,
    });
  }

  /**
   * Push 1-2 expanding rings out from a hit point. Big-combo perfects
   * (>= {@link SHOCKWAVE_DOUBLE_RING_COMBO}) get a second, layered
   * ring for extra punch; reduced-motion users always get a single
   * smaller / shorter ring.
   *
   * Sized off the chart's circle radius so the rings track the same
   * canvas-relative scale as the circles they emerge from.
   */
  private spawnHitShockwaves(
    x: number,
    y: number,
    verdict: 'perfect' | 'good' | 'ok',
    newCombo: number,
  ): void {
    const baseR = this.renderer.osuLengthToCss(this.chart.circleRadius);
    const verdictColour =
      verdict === 'perfect' ? '#3bff9d' : verdict === 'good' ? '#16e0ff' : '#ffd84d';
    const tuning =
      verdict === 'perfect' ? SHOCKWAVE.perfect : verdict === 'good' ? SHOCKWAVE.good : SHOCKWAVE.ok;
    const shrink = this.reducedMotion ? 0.5 : 1;
    this.shockwaves.push({
      x,
      y,
      startR: baseR * tuning.startMul,
      endR: baseR * tuning.endMul * shrink,
      life: tuning.life * shrink,
      maxLife: tuning.life * shrink,
      thickness: Math.max(2, baseR * tuning.thicknessMul),
      colour: verdictColour,
      alpha: tuning.alpha * (this.reducedMotion ? 0.6 : 1),
    });
    if (
      verdict === 'perfect' &&
      newCombo >= SHOCKWAVE_DOUBLE_RING_COMBO &&
      !this.reducedMotion
    ) {
      const sec = SHOCKWAVE.perfectSecondary;
      this.shockwaves.push({
        x,
        y,
        startR: baseR * sec.startMul,
        endR: baseR * sec.endMul,
        life: sec.life,
        maxLife: sec.life,
        thickness: Math.max(1.5, baseR * sec.thicknessMul),
        colour: verdictColour,
        alpha: sec.alpha,
      });
    }
  }

  /**
   * Red "thud" shockwave at the missed circle's centre. Always
   * spawned on `verdict === 'miss'`; a second, larger
   * {@link SHOCKWAVE.comboBreak} ring is stacked when the broken
   * combo was sizeable (>= {@link SHOCKWAVE_DOUBLE_RING_COMBO}) so
   * the moment a hot streak snaps gets dramatic visual weight to
   * match the audio combo-break sting.
   *
   * Reduced-motion shrinks the rings (life + size scaled down) so
   * the cue stays informative without throwing motion at the user.
   */
  private spawnMissShockwaves(x: number, y: number, previousCombo: number): void {
    const baseR = this.renderer.osuLengthToCss(this.chart.circleRadius);
    const missColour = '#ff3b5c';
    const shrink = this.reducedMotion ? 0.6 : 1;
    const m = SHOCKWAVE.miss;
    this.shockwaves.push({
      x,
      y,
      startR: baseR * m.startMul,
      endR: baseR * m.endMul * shrink,
      life: m.life * shrink,
      maxLife: m.life * shrink,
      thickness: Math.max(2, baseR * m.thicknessMul),
      colour: missColour,
      alpha: m.alpha * (this.reducedMotion ? 0.6 : 1),
    });
    if (previousCombo >= SHOCKWAVE_DOUBLE_RING_COMBO && !this.reducedMotion) {
      const cb = SHOCKWAVE.comboBreak;
      this.shockwaves.push({
        x,
        y,
        startR: baseR * cb.startMul,
        endR: baseR * cb.endMul,
        life: cb.life,
        maxLife: cb.life,
        thickness: Math.max(1.5, baseR * cb.thicknessMul),
        colour: missColour,
        alpha: cb.alpha,
      });
    }
  }

  /**
   * Cursor-tap shockwave: a small white-ish ring at the click point
   * that gives the player immediate "your tap registered" feedback,
   * regardless of whether it hit a circle. Skipped under
   * reduced-motion (decorative-only — the underlying input already
   * has audio/HUD feedback that doesn't depend on this ring).
   */
  private spawnCursorShockwave(x: number, y: number): void {
    if (this.reducedMotion) {
      return;
    }
    const c = SHOCKWAVE.cursor;
    this.shockwaves.push({
      x,
      y,
      startR: c.startR,
      endR: c.endR,
      life: c.life,
      maxLife: c.life,
      thickness: c.thickness,
      colour: '#ffffff',
      alpha: c.alpha,
    });
  }

  /**
   * Score-pop: a bold "+N" flyer that arcs from the hit position
   * toward the HUD score. `newCombo` is `previousCombo + 1` — matches
   * the engine's post-increment combo used in the score formula so
   * the displayed gain agrees with what `state.score` actually moves
   * by. Reduced-motion still spawns the pop (it's information, not
   * decoration) but the renderer flattens its arc.
   */
  private spawnScorePop(
    x: number,
    y: number,
    verdict: 'perfect' | 'good' | 'ok',
    newCombo: number,
  ): void {
    const baseScore =
      verdict === 'perfect' ? PERFECT_SCORE : verdict === 'good' ? GOOD_SCORE : OK_SCORE;
    const gain = Math.round(baseScore * comboMultiplier(newCombo));
    if (gain <= 0) {
      return;
    }
    const big = verdict === 'perfect' && newCombo >= SHOCKWAVE_DOUBLE_RING_COMBO;
    const colour =
      verdict === 'perfect' ? '#3bff9d' : verdict === 'good' ? '#16e0ff' : '#ffd84d';
    const scoreAnchor = this.renderer.getHudScoreAnchor();
    this.scorePops.push({
      text: `+${gain}`,
      startX: x,
      startY: y,
      targetX: scoreAnchor.x,
      targetY: scoreAnchor.y,
      colour,
      big,
      life: SCORE_POP_LIFE,
      maxLife: SCORE_POP_LIFE,
    });
  }

  private addShake(amount: number): void {
    this.shake.amount = Math.max(this.shake.amount, amount);
    this.shake.decay = amount * 6;
  }

  /**
   * Translate one engine `spinner_tick` event into client-side juice:
   *
   *  - Coalesced "+N" floating pop at playfield centre — accumulates
   *    `scoreDelta` across consecutive ticks and only spawns when the
   *    bucket exceeds {@link SPINNER_TICK_POP_THRESHOLD} or
   *    {@link SPINNER_TICK_POP_INTERVAL_MS} has elapsed since the
   *    last pop. Without this guard the per-frame capture (60 Hz on
   *    a moving cursor) would spawn ~60 floats/sec per active spinner
   *    and stack ~42 of them on screen at any moment.
   *  - One whoosh + flash + shake per crossed full-rotation boundary
   *    so each completed spin lands as a discrete reward, even when
   *    a batched step delivers several radians at once.
   *  - "x{N} ROTATIONS!" floating text at every full-rotation
   *    milestone so chasing the next whole spin reads clearly even at
   *    high RPS.
   *
   * Per-rotation feedback is gated by {@link spinnerLastNotifiedRotations}
   * so resyncing replays (which can deliver many samples in one
   * batched step) don't spam stacked SFX. The map is cleared on loop
   * restart in {@link startNextLoop}.
   */
  private handleSpinnerTick(payload: CirclesSpinTickPayload): void {
    const center = this.renderer.playfieldToCss(
      OSU_PLAYFIELD_WIDTH / 2,
      OSU_PLAYFIELD_HEIGHT / 2,
    );
    const wholeRotations = Math.floor(payload.rotations / (2 * Math.PI));
    const lastNotified = this.spinnerLastNotifiedRotations.get(payload.hitObjectId) ?? 0;
    const intensity = Math.min(1, Math.max(0, payload.rps / SPINNER_INTENSITY_SATURATION_RPS));

    if (payload.scoreDelta > 0) {
      const bucket = this.spinnerScorePopBuckets.get(payload.hitObjectId) ?? {
        accumulated: 0,
        lastPopAt: 0,
      };
      bucket.accumulated += payload.scoreDelta;
      const nowMs = performance.now();
      const elapsedMs = nowMs - bucket.lastPopAt;
      if (
        bucket.accumulated >= SPINNER_TICK_POP_THRESHOLD ||
        elapsedMs >= SPINNER_TICK_POP_INTERVAL_MS
      ) {
        const colour = spinIntensityColour(intensity);
        const scale = 0.55 + intensity * 0.45;
        this.spawnFloatingText(
          `+${bucket.accumulated}`,
          center.x,
          center.y,
          colour,
          { vy: -80, scale },
        );
        bucket.accumulated = 0;
        bucket.lastPopAt = nowMs;
      }
      this.spinnerScorePopBuckets.set(payload.hitObjectId, bucket);
    }

    if (wholeRotations > lastNotified) {
      const newWholes = wholeRotations - lastNotified;
      this.spinnerLastNotifiedRotations.set(payload.hitObjectId, wholeRotations);
      this.spawnFloatingText(
        `x${wholeRotations} ROTATIONS!`,
        center.x,
        center.y - 40,
        '#3bff9d',
        { big: true, vy: -90 },
      );
      this.flashes.push({ type: 'good', t: 0.18, intensity: 0.8 + intensity * 0.6 });
      // Cap stacked whooshes at 3 even if a freak step crosses many
      // rotations at once, so a worst-case batch can't blast a buzz
      // of overlapping whoosh SFX.
      const whooshCount = Math.min(3, newWholes);
      for (let i = 0; i < whooshCount; i++) {
        this.audio.scheduleFlip();
      }
      this.addShake(3 + intensity * 4);
    }

    // Clear-bonus rotation: each full rotation past the spinner's
    // bonus threshold (post-clear in osu! parlance) lands a discrete
    // golden burst. The engine already added `bonusScoreDelta` to
    // `state.score` (it's a subset of `payload.scoreDelta`); we just
    // mirror the same number in the visual pop so the player reads
    // exactly what they earned, even when a high-RPS sample crosses
    // multiple bonus boundaries at once.
    if (payload.bonusScoreDelta > 0) {
      this.spawnFloatingText(
        `+${payload.bonusScoreDelta}`,
        center.x,
        center.y,
        '#ffd84d',
        { big: true, vy: -110, scale: 1.3 },
      );
      this.flashes.push({ type: 'good', t: 0.25, intensity: 1.2, colour: '#ffd84d' });
      this.particleEmissions.push({
        type: 'milestone',
        x: center.x,
        y: center.y,
        colour: '#ffd84d',
      });
      this.audio.scheduleComboMilestone();
      this.addShake(6);
    }
  }

  /**
   * Translate one engine slider component event (`slider_tick`,
   * `slider_repeat`, or `slider_tail`) into client juice. The
   * `tracking=false` fall-through case from the engine still fires
   * an event with `scoreDelta=0` — we render a tiny missed-tick cue
   * so the player learns that letting go of the button mid-slider
   * has a cost, even though combo isn't broken.
   *
   * Renders the cue at the slider ball's curve position so it lands
   * where the player's eye already is (roughly along the curve at
   * the tick beat). Spawning at the slider's head would land too
   * far behind the action on long sliders.
   */
  private handleSliderTick(
    kind: 'slider_tick' | 'slider_repeat' | 'slider_tail',
    payload: CirclesSliderTickPayload,
  ): void {
    const obj = this.chart.hitObjects.find((h) => h.id === payload.hitObjectId);
    if (!obj || !obj.slider) {
      return;
    }
    // Slider ball position lands somewhere along the curve at the
    // event's beat. We don't have the geometric path on the session
    // side (the renderer caches it), so as a serviceable visual
    // anchor we use the slider head — players read tick credit as
    // "this slider is going well", they don't need pixel-perfect
    // ball-tracked cues for every tick.
    const stackOff = getStackOffsets(this.chart).get(obj.id);
    const xFx = obj.x + (stackOff?.dx ?? 0);
    const yFx = obj.y + (stackOff?.dy ?? 0);
    const { x, y } = this.renderer.playfieldToCss(xFx, yFx);

    if (!payload.tracking) {
      // Tracking-loss fall-through: subtle cue that the player
      // dropped tracking. No combo break, no SFX — the renderer's
      // dimmed follow circle already communicates the state, and
      // stacking miss SFX on every tick would be punishing.
      if (kind === 'slider_tail') {
        // While we still have teaching budget, replace the terse
        // "TAIL MISS" label with an instruction that tells the player
        // what to do differently next time. Capped per run so it
        // doesn't nag once they've got the idea.
        if (this.holdSliderHintsShown < this.maxTutorialHints) {
          this.holdSliderHintsShown += 1;
          this.spawnFloatingText('HOLD TO THE END!', x, y, '#ffae3b', {
            big: true,
            vy: -60,
          });
        } else {
          this.spawnFloatingText('TAIL MISS', x, y, '#ffae3b', { vy: -28 });
        }
      }
      return;
    }

    if (kind === 'slider_tail') {
      // Tail credit lands as a small "PERFECT!" — sliders don't
      // distinguish good/perfect tails (osu collapses the
      // judgement on the head), but we want the player to feel the
      // payoff for holding through the whole curve.
      this.spawnFloatingText('+', x, y, '#3bff9d', { scale: 1.05 });
      this.flashes.push({ type: 'good', t: 0.18, intensity: 0.6 });
      this.audio.scheduleHitFeedback('good', undefined, {
        pan: panForCirclesX(xFx),
        comboNote: this.comboNoteCounter,
      });
      this.comboNoteCounter += 1;
    } else {
      // Tick / repeat: small floating "+30" / "+50" so the player
      // sees the chunks they're earning — too quiet on its own
      // would make sliders feel scoreless. Half-strength compared
      // to a full hit: tick scoring is meant to be background
      // gravy, not a parade.
      const colour = '#16e0ff';
      this.floatingTexts.push({
        text: `+${payload.scoreDelta}`,
        x: x + (Math.random() - 0.5) * 18,
        y: y - 10,
        colour,
        vy: -38,
        scale: 0.7,
        life: 0.4,
        maxLife: 0.4,
        big: false,
      });
      // Repeat events are rarer and more meaningful than ticks
      // (each one signals the slider literally reversed direction).
      // Give them a faint flash; ticks stay silent to avoid buzz.
      if (kind === 'slider_repeat') {
        this.flashes.push({ type: 'good', t: 0.12, intensity: 0.4 });
      }
    }
  }

  private spawnMilestoneFx(kind: MilestoneKind): void {
    const isStar = milestoneIsStar(kind);
    const tier = milestoneTier(kind);
    const colour = isStar ? '#ffd84d' : '#ffb13b';
    const { x, y } = this.renderer.playfieldToCss(256, 192);

    this.particleEmissions.push({ type: 'milestone', x, y, colour });
    this.flashes.push({
      type: 'milestone',
      t: tier === 3 ? 0.6 : 0.4,
      intensity: 0.8 + tier * 0.2,
      colour,
    });
    const glyph = isStar ? '\u2605' : '\u{1F3C6}';
    this.spawnFloatingText(`${glyph} x${tier}`, x, y, colour, {
      big: true,
      vy: -90,
      scale: tier === 3 ? 1.4 : 1.15,
    });
    this.addShake(tier === 3 ? 14 : 6 + tier * 2);
    this.audio.playMilestoneFanfare(tier);
    if (kind === 'star_3') {
      this.audio.duckMusic(0.12, 350);
      this.audio.playVictorySting();
    }
  }

  private handleEvent(ev: CirclesEngineEvent): void {
    if (ev.type === 'object_resolved') {
      const res = ev.payload as CircleResolution;
      const hitObj = this.chart.hitObjects.find((h) => h.id === res.hitObjectId);
      // Apply the renderer's visual stack offset so the burst lands
      // on the rendered (offset) circle rather than the engine's raw
      // hit-detection centre. Spinners and non-stacked circles have
      // no entry in the offset map and fall back to the raw centre.
      const stackOff = getStackOffsets(this.chart).get(res.hitObjectId);
      const xFx = (hitObj?.x ?? 256) + (stackOff?.dx ?? 0);
      const yFx = (hitObj?.y ?? 192) + (stackOff?.dy ?? 0);
      const { x, y } = this.renderer.playfieldToCss(xFx, yFx);
      this.particleEmissions.push({
        type:
          res.verdict === 'perfect'
            ? 'hit_perfect'
            : res.verdict === 'good'
              ? 'hit_good'
              : res.verdict === 'ok'
                ? 'hit_ok'
                : 'hit_miss',
        x,
        y,
      });

      // The engine attaches the pre-resolution combo on every
      // `CircleResolution` (via `previousCombo`). Reading it directly
      // from the payload is robust to multiple events firing inside a
      // single `step()` call — `state.combo` only ever reflects the
      // post-step value.
      const previousCombo = res.previousCombo;
      const isComboBreak = res.verdict === 'miss' && previousCombo >= 5;
      const isBigComboBreak = res.verdict === 'miss' && previousCombo >= 25;

      // Flash duration / intensity: perfects feel meaty, goods are
      // baseline, oks are subtle (the player barely caught it — the
      // mercy band shouldn't celebrate too loudly), big combo breaks
      // dramatise the lost streak.
      const flashT =
        isBigComboBreak
          ? 0.45
          : res.verdict === 'perfect'
            ? 0.3
            : res.verdict === 'ok'
              ? 0.16
              : 0.22;
      const flashIntensity =
        isBigComboBreak ? 1.4 : res.verdict === 'perfect' ? 1.2 : res.verdict === 'ok' ? 0.7 : 1;
      this.flashes.push({
        type: res.verdict,
        t: flashT,
        intensity: flashIntensity,
      });

      const label =
        res.verdict === 'perfect'
          ? 'PERFECT!'
          : res.verdict === 'good'
            ? 'Good'
            : res.verdict === 'ok'
              ? 'OK'
              : 'Miss';
      const colour =
        res.verdict === 'perfect'
          ? '#3bff9d'
          : res.verdict === 'good'
            ? '#16e0ff'
            : res.verdict === 'ok'
              ? '#ffd84d'
              : '#ff3b5c';
      const comboScale = res.verdict === 'perfect' && this.state.combo >= 50 ? 1.4 : 1;
      this.spawnFloatingText(label, x, y, colour, { scale: comboScale });

      if (res.verdict !== 'miss') {
        this.spawnHitShockwaves(x, y, res.verdict, previousCombo + 1);
        this.spawnScorePop(x, y, res.verdict, previousCombo + 1);
        // Hit-error meter only sees successful hits — misses use the
        // auto-resolution beat as their "tap time" which has no
        // relation to actual player input, so excluding them keeps
        // the meter readable.
        this.renderer.recordHitTiming(res.diffMs, res.verdict);
      }

      if (res.verdict === 'miss') {
        // Red "thud" ring at the missed centre. Fires on every miss
        // (not just combo breaks) so a stream of misses still produces
        // expanding visual feedback in addition to particles + shake.
        this.spawnMissShockwaves(x, y, previousCombo);
        this.addShake(isBigComboBreak ? 12 : 4);
        this.comboMilestonesHit.clear();
        // Reset the pentatonic so the next perfect chain starts back
        // at the root note. The SFX already ignores `comboNote` for
        // the miss variant; this is so the *next* hit reads as a
        // fresh chain musically, not a continuation.
        this.comboNoteCounter = 0;
        if (isComboBreak) {
          this.audio.scheduleComboBreak();
        }
        // Pulse-duck the music so the miss SFX cuts through. Big
        // combo breaks dip deeper / longer to sell the lost streak;
        // regular misses get a quick dim that auto-restores.
        const duck = isBigComboBreak ? MUSIC_DUCK_COMBO_BREAK : MUSIC_DUCK_MISS;
        this.audio.duckMusicPulse(duck.dip, duck.attackMs, duck.releaseMs);
      } else if (res.verdict === 'perfect') {
        this.addShake(4);
      }

      const pan = panForCirclesX(xFx);
      // Layer the imported osu! hitsound accents (whistle/finish/clap)
      // this note carries on top of the base click. `hitObj.hitSound`
      // is absent on plain notes, so unhitsounded maps sound exactly as
      // before. The accent timbre comes from the chart-wide
      // `hitSampleSet` bank. Skips on miss inside the audio engine.
      this.audio.scheduleCirclesHitFeedback(
        res.verdict,
        undefined,
        hitObj?.hitSound,
        this.chart.hitSampleSet,
        { pan, comboNote: this.comboNoteCounter },
      );
      // Advance the pentatonic only on successful hits; miss already
      // reset above. Wraps via the SFX's modulo on COMBO_PENTATONIC_LENGTH.
      if (res.verdict !== 'miss') {
        this.comboNoteCounter += 1;
      }

      for (const milestone of COMBO_MILESTONES) {
        if (this.state.combo === milestone && !this.comboMilestonesHit.has(milestone)) {
          this.comboMilestonesHit.add(milestone);
          const center = this.renderer.playfieldToCss(256, 192);
          this.spawnFloatingText(`x${milestone} COMBO!`, center.x, center.y, '#ff2bd6', {
            big: true,
            vy: -80,
          });
          this.audio.scheduleComboMilestone();
          this.addShake(3);
        }
      }
    } else if (ev.type === 'circle_shake') {
      const payload = ev.payload as CirclesCircleShakePayload;
      const hitObj = this.chart.hitObjects.find((h) => h.id === payload.hitObjectId);
      const stackOff = getStackOffsets(this.chart).get(payload.hitObjectId);
      const xFx = (hitObj?.x ?? 256) + (stackOff?.dx ?? 0);
      const yFx = (hitObj?.y ?? 192) + (stackOff?.dy ?? 0);
      const { x, y } = this.renderer.playfieldToCss(xFx, yFx);
      // Ask the renderer to jitter the circle for ~150ms — purely
      // visual, no engine state mutated. Floats a "TOO EARLY!" cue
      // at the click target so the player learns the timing.
      this.renderer.registerShake(payload.hitObjectId);
      this.spawnFloatingText('TOO EARLY!', x, y, '#ffae3b', { vy: -40 });
    } else if (ev.type === 'spinner_resolved') {
      const center = this.renderer.playfieldToCss(256, 192);
      this.particleEmissions.push({ type: 'milestone', x: center.x, y: center.y, colour: '#16e0ff' });
      this.flashes.push({ type: 'good', t: 0.3, intensity: 1 });
      // Spinners auto-resolve at the playfield centre — pan stays at
      // 0, and we hold the pentatonic step rather than advancing
      // (an auto-good is partial credit, not a melodic milestone).
      this.audio.scheduleHitFeedback('good', undefined, {
        pan: 0,
        comboNote: this.comboNoteCounter,
      });
    } else if (ev.type === 'spinner_tick') {
      this.handleSpinnerTick(ev.payload as CirclesSpinTickPayload);
    } else if (
      ev.type === 'slider_tick' ||
      ev.type === 'slider_repeat' ||
      ev.type === 'slider_tail'
    ) {
      this.handleSliderTick(ev.type, ev.payload as CirclesSliderTickPayload);
    } else if (ev.type === 'tap') {
      const payload = ev.payload as { x: number; y: number; hit: boolean | 'early' };
      const { x, y } = this.renderer.playfieldToCss(payload.x, payload.y);
      // Cursor shockwave fires on every tap (hit, miss, early) so the
      // player sees their input land. Skipped under reduced-motion.
      this.spawnCursorShockwave(x, y);
      // Empty taps and early clicks also push a small particle puff —
      // gives missed clicks distinct feedback on top of the cursor
      // ring, while hits lean on the much bigger circle-centred burst
      // from the `object_resolved` branch above.
      if (payload.hit !== true) {
        this.particleEmissions.push({ type: 'tap', x, y });
      }
    } else if (ev.type === 'hp_changed') {
      const payload = ev.payload as CirclesHpChangedPayload;
      const { x, y } = this.renderer.playfieldToCss(256, 192);
      const drained = payload.reason === 'drained';
      if (drained) {
        this.particleEmissions.push({ type: 'death', x, y });
        this.flashes.push({ type: 'death', t: 0.6, intensity: 1.5 });
        this.spawnFloatingText('GAME OVER', x, y, '#ff3b5c', { big: true, vy: -30 });
        this.addShake(14);
        this.audio.playDeath();
      } else if (payload.reason === 'lifeline') {
        this.spawnFloatingText('LIFELINE!', x, y, '#3bd8ff', {
          big: true,
          vy: -50,
        });
        this.flashes.push({ type: 'death', t: 0.45, intensity: 1.0 });
        this.addShake(12);
        this.audio.playDeath();
      } else if (payload.delta < 0) {
        const isEmptyTap = payload.reason === 'empty_tap';
        const showEmptyTapToast =
          isEmptyTap && this.clickCircleHintsShown < this.maxTutorialHints;
        // Severity-scaled vignette: a 1-HP nibble shouldn't feel the
        // same as a 10-HP miss. Intensity ramps with damage size,
        // clamped so worst-case isn't blinding.
        const damageAmount = Math.abs(payload.delta);
        const damageIntensity = Math.min(1.4, 0.45 + damageAmount * 0.06);
        const damageT = Math.min(0.45, 0.22 + damageAmount * 0.012);
        this.flashes.push({ type: 'hp_loss', t: damageT, intensity: damageIntensity });
        if (showEmptyTapToast) {
          this.clickCircleHintsShown += 1;
          this.spawnFloatingText('CLICK ON THE CIRCLE!', x, y, '#ffae3b', {
            big: true,
            vy: -60,
          });
        } else {
          // Damage floater anchors at the HUD HP bar (not the
          // playfield) so the "-N HP" label rises toward the same
          // visual element the bleed-segment / bar-shake / numeric
          // pulse all fire on. Co-locating every piece of damage
          // feedback at one spot makes the cost-to-life feel
          // coherent — players don't have to scan two halves of the
          // screen to read a single damage event. The per-circle
          // "Miss" label already lives over the missed circle, so we
          // don't lose spatial attribution by moving this one.
          // offsetY=0 because the HUD anchor is already at the
          // floater's intended canvas position.
          const damageAnchor = this.renderer.getHudHpDamageAnchor();
          this.spawnFloatingText(
            `${payload.delta} HP`,
            damageAnchor.x,
            damageAnchor.y,
            '#ff3b5c',
            {
              big: true,
              vy: -55,
              scale: 0.95,
              offsetY: 0,
            },
          );
        }
        this.addShake(4 + Math.min(8, damageAmount * 0.4));
      }
    }
  }

  private finishSession(status: CirclesSessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      // Run is "completed" once at least one full loop is cleared,
      // mirroring the server's `loopsCompleted > 0` rule. A player
      // who dies on loop 5 still counts as having completed the
      // chart for leaderboard / progression purposes.
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      oks: this.state.oks,
      misses: this.state.misses,
      accuracy: computeAccuracy(
        this.state.perfects,
        this.state.goods,
        this.state.oks,
        this.state.misses,
      ),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    this.audio.stopMusic();
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): CirclesGameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
    };
  }
}

export interface CirclesGameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => CirclesSessionStatus;
  getState: () => CirclesEngineState;
  getBeat: () => number;
  getEvents: () => CirclesEngineEvent[];
  isAudioStarted: () => boolean;
}

export { buildPerfectCirclesReplayInputs } from '@shared/engine/circles/headlessRunner';
